import React, { useEffect, useState } from "react";
import { FaCheck, FaRegEdit, FaRegEye } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Link } from "react-router-dom";
import TableSkeleton from "../../TableSkeleton";
import Pagination from "../../Shared/Pagination";

const DepartmentTable = ({
  data,
  searchTerm,
  itemsPerPage = 10,
  selectedLanguage = "en",
  handleSearch,
  handleEdit,
  setData,
  openDeleteModal,
  doctors = [],
  isLoading = false,
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [isHeaderChecked, setIsHeaderChecked] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Initialize selectedRows and currentPage based on data length
  useEffect(() => {
    setSelectedRows(new Array(data.length).fill(false));
    setCurrentPage(1); // Reset to first page when data changes
  }, [data.length]);

  // Update header checkbox state
  useEffect(() => {
    const currentPageStart = (currentPage - 1) * itemsPerPage;
    const currentPageEnd = Math.min(currentPageStart + itemsPerPage, data.length);
    const visibleRowsSelected = selectedRows
      .slice(currentPageStart, currentPageEnd)
      .every((isSelected) => isSelected && isSelected !== undefined);
    setIsHeaderChecked(
      data.length > 0 && currentPageEnd > currentPageStart && visibleRowsSelected
    );
  }, [selectedRows, currentPage, data.length, itemsPerPage]);

  // Adjust currentPage when data length changes
  useEffect(() => {
    const totalPages = Math.ceil(data.length / itemsPerPage) || 1;
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [data.length, itemsPerPage, currentPage]);

   useEffect(() => {
      setTotalRecords(data.length);
    }, [data.length]);

  // Filter departments based on search term
  const filterDepartments = () => {
    if (!searchTerm) return data;
    return data.filter((department) => {
      const id = department.id?.toLowerCase() || "";
      const name =
        department.translations?.[selectedLanguage]?.name?.toLowerCase() || "";
      const metaTitle =
        department.translations?.[selectedLanguage]?.metaTitle?.toLowerCase() ||
        "";
      return (
        id.includes(searchTerm) ||
        name.includes(searchTerm) ||
        metaTitle.includes(searchTerm)
      );
    });
  };

  const handleHeaderCheckboxChange = () => {
    const newSelectedRows = [...selectedRows];
    const currentPageStart = (currentPage - 1) * itemsPerPage;
    const currentPageEnd = Math.min(
      currentPageStart + itemsPerPage,
      data.length
    );
    const allChecked = currentData.every(
      (_, index) => selectedRows[currentPageStart + index]
    );
    for (let i = currentPageStart; i < currentPageEnd; i++) {
      newSelectedRows[i] = !allChecked;
    }
    setSelectedRows(newSelectedRows);
    setIsHeaderChecked(!allChecked);
  };

  const handleRowCheckboxChange = (index) => {
    const newSelectedRows = [...selectedRows];
    newSelectedRows[index] = !newSelectedRows[index];
    setSelectedRows(newSelectedRows);
  };

  // Pagination logic
  const filteredData = filterDepartments();
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Define skeleton columns to match table structure
  const skeletonColumns = [
    { width: 16, height: 16, className: "p-3", skeletonClassName: "ml-3" }, // Checkbox
    { width: 40, height: 14, className: "p-3" }, // SL No
    { width: 80, height: 14, className: "p-3" }, // ID
    { width: 200, height: 14, className: "p-3" }, // Department Name
    { width: 150, height: 14, className: "p-3" }, // Meta Title
    { width: 60, height: 14, className: "p-3" }, // Doctor Count
    {
      width: 100,
      height: 28,
      className: "p-3 text-center",
      skeletonClassName: "inline-block",
    }, // Actions
  ];

  return (
    <div className="mt-6">
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-col gap-3 sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700">
              Department List
            </h2>
            {selectedRows.some((isSelected) => isSelected) && (
              <button
                onClick={() => {
                  const selectedIds = data
                    .map((dept, index) =>
                      selectedRows[index] ? dept.id : null
                    )
                    .filter((id) => id !== null);
                  openDeleteModal(selectedIds);
                }}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="inline-flex flex-col sm:flex-row items-center justify-end gap-3 flex-1">
            <input
              type="text"
              className="sm:max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by name, meta title"
              value={searchTerm}
              onChange={handleSearch}
            />
            <Link
              to="/add-department"
              className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 w-full sm:w-auto text-center"
            >
              Add Department
            </Link>
          </div>
        </div>
        {/* Table Area */}
        <div className="overflow-x-auto">
          <table className="min-w-[1000px] w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-[#FBFBFD]">
                <th className="p-3 text-left">
                  <div className="relative left-3 -top-[2px]">
                    <input
                      type="checkbox"
                      id="agreement"
                      className="hidden peer"
                      checked={isHeaderChecked}
                      onChange={handleHeaderCheckboxChange}
                    />
                    <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                      <FaCheck className="text-slate-100" />
                    </span>
                    <label
                      htmlFor="agreement"
                      className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                    ></label>
                  </div>
                </th>
                <th className="p-3 text-left text-sm font-semibold text-gray-700">
                  SL No
                </th>
                <th className="p-3 text-left text-sm font-semibold text-gray-700">
                  ID
                </th>
                <th className="p-3 text-left text-sm font-semibold text-gray-700">
                  Department Name
                </th>
                <th className="p-3 text-left text-sm font-semibold text-gray-700">
                  Meta Title
                </th>
                <th className="p-3 text-left text-sm font-semibold text-gray-700">
                  Doctor
                </th>
                <th className="p-3 text-center text-sm font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <TableSkeleton columns={skeletonColumns} rowCount={5} />
              ) : currentData.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-4 text-center">
                    No records found.
                  </td>
                </tr>
              ) : (
                currentData.map((department, index) => {
                  const serialNumber = indexOfFirstItem + index + 1;
                  const translation =
                    department.translations?.[selectedLanguage] || {};
                  const deptName = (
                    translation.name ||
                    department.name ||
                    "N/A"
                  ).trim();

                  // Calculate doctor count
                  const doctorCount = doctors.filter((doc) => {
                    const lang = selectedLanguage || "en";
                    const docDept = doc.translations?.[lang]?.department;
                    if (!docDept) return false;
                    return (
                      docDept.trim().toLowerCase() === deptName.toLowerCase()
                    );
                  }).length;

                  return (
                    <tr
                      key={department.id}
                      className="border-b hover:bg-gray-50"
                    >
                      <td className="p-3">
                        <div className="relative left-3 -top-[1px]">
                          <input
                            type="checkbox"
                            id={`doc${indexOfFirstItem + index}`}
                            className="hidden peer"
                            checked={
                              selectedRows[indexOfFirstItem + index] || false
                            }
                            onChange={() =>
                              handleRowCheckboxChange(indexOfFirstItem + index)
                            }
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor={`doc${indexOfFirstItem + index}`}
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6"
                          ></label>
                        </div>
                      </td>
                      <td className="p-3 text-sm">{serialNumber}</td>
                      <td className="p-3 text-sm">
                        {department.id.slice(0, 8)}
                      </td>
                      <td className="p-3 text-sm">
                        <div className="flex items-center gap-2">
                          <img
                            src={`https://api.muktihospital.com/${department.icon}`}
                            alt={translation.title || deptName}
                            className="size-8 rounded-md shrink-0"
                            onError={(e) => (e.target.src = "/placeholder.png")} // Fallback image
                          />
                          {deptName}
                        </div>
                      </td>
                      <td className="p-3 text-sm">
                        {translation.metaTitle || "N/A"}
                      </td>
                      <td className="p-3 text-sm">{doctorCount}</td>
                      <td className="p-3 text-center">
                        <div className="flex justify-center gap-2">
                          <Link
                            to={`https://muktihospital.com/department/${department.slug}`}
                            className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                            title="View"
                          >
                            <FaRegEye />
                          </Link>
                          <button
                            onClick={() => handleEdit(department.id)}
                            className="size-7 bg-M-Green-color/10 text-M-Green-color hover:bg-M-Green-color hover:text-white rounded-full flex items-center justify-center transition"
                            title="Edit"
                          >
                            <FaRegEdit />
                          </button>
                          <button
                            onClick={() => openDeleteModal([department.id])}
                            className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white rounded-full flex items-center justify-center transition"
                            title="Delete"
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalRecords={filteredData.length}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default DepartmentTable;